import * as React from "react";
import { CommandList } from "cmdk";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button, type ButtonProps } from "@/components/_atoms/button";
import { Checkbox } from "@/components/_atoms/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/_atoms/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/_atoms/dialog";
import { Label } from "@/components/_atoms/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/_atoms/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_atoms/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_atoms/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/_atoms/tooltip";
import { useParseCsv } from "@/hooks/use-parse-csv";
// import { useUploadFile } from "@/hooks/use-upload-file";
import { cn } from "@/lib/cn";
import { ArrowLeftIcon, CaretSortIcon, CheckIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons";

import { FileUploader } from "./file-uploader";

export interface CsvField {
  /**
   * Field display label shown to the user.
   * @example "Name"
   */
  label: string;

  /**
   * Key identifying the field in the imported data.
   * @example "name"
   */
  value: string;

  /**
   * Optional flag indicating if the field is required.
   * Required fields cannot be unchecked during mapping.
   * @default false
   * @example true
   */
  required?: boolean;

  /**
   * Optional flag indicating if the field is a date field.
   * @default undefined
   */
  validation?: (cellValue: string) => { success: boolean; message?: string };

  /**
   * An optional note displayed in the header column of the table
   * @default false
   * @example "dd.mm.yyyy"
   */
  note?: string;

  /**
   * Optional list of allowed values for this field.
   * When provided, cells render as a dropdown select instead of plain text.
   * @example options={[{ label: "Option A", value: "a" }]}
   */
  options?: { label: string; value: string }[];
  normalizeValue?: (value: string) => string;
}

interface CsvImporterProps extends React.ComponentPropsWithoutRef<typeof DialogTrigger>, ButtonProps {
  strict?: boolean;
  allowXlsx?: boolean;
  uploadTitle?: string;
  rowLimit?: number;
  maxFileSize?: number;
  extraContent?: React.ReactNode;
  isImportDisabled?: boolean;
  validateData?: (data: Record<string, unknown>[]) => string | null;
  onClose?: () => void;
  /**
   * Array of field mappings defining the imported data structure.
   * Each includes a label, value, and optional required flag.
   * @example fields={[{ label: 'Name', value: 'name', required: true }, { label: 'Email', value: 'email' }]}
   */
  fields: CsvField[];

  /**
   * Callback function called on data import.
   * Receives an array of records as key-value pairs.
   * @example onImport={(data) => console.log(data)}
   */
  onImport: (
    data: Record<string, unknown>[],
    completed: ({ success, message }: { success: boolean; message?: string }) => void,
  ) => void | Promise<void>;
  children?: React.ReactNode;
}

export function CsvImporter({
  fields,
  onImport,
  children,
  strict = false,
  allowXlsx = false,
  uploadTitle,
  rowLimit = 1001,
  maxFileSize = 4 * 1024 * 1024,
  extraContent,
  isImportDisabled,
  validateData,
  onClose,
}: CsvImporterProps) {
  const { t } = useTranslation("common");
  const [loading, setLoading] = React.useState(false);
  const [hasImportError, setHasImportError] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<"upload" | "map">("upload");
  const {
    data,
    isParsing,
    fieldMappings,
    onParse,
    onFieldChange,
    onFieldToggle,
    onFieldsReset,
    onCellChange,
    getSanitizedData,
    error,
  } = useParseCsv({
    fields,
    strict,
    allowXlsx,
  });

  const isBusy = loading || isParsing;
  const isLocked = isBusy || isImportDisabled;

  const normalizedData = data.map((row) => ({
    ...row,
    ...Object.fromEntries(
      fields
        .filter((field) => fieldMappings.current[field.value] && field.normalizeValue)
        .map((field) => [field.value, field.normalizeValue!(String(row[field.value] ?? ""))]),
    ),
  }));
  const validationErrors: Record<string, Record<number, string>> = {};
  fields.forEach((field) => {
    if (!fieldMappings.current[field.value]) return;
    normalizedData.forEach((row, index) => {
      const cellValue = String(row[field.value] ?? "");
      const validationResult = field.validation?.(cellValue);
      const hasInvalidOption = strict && field.options && !field.options.some((option) => option.value === cellValue);
      if (hasInvalidOption || (validationResult && !validationResult.success)) {
        validationErrors[field.value] ??= {};
        validationErrors[field.value][index] = validationResult?.message || t("csv.invalidValue");
      }
    });
  });

  const hasColumnErrors = (fieldValue: string) => {
    return validationErrors[fieldValue] && Object.keys(validationErrors[fieldValue]).length > 0;
  };

  const importData = strict
    ? normalizedData.map((row) =>
        Object.fromEntries(
          fields
            .filter((field) => fieldMappings.current[field.value])
            .map((field) => [field.value, row[field.value] ?? ""]),
        ),
      )
    : getSanitizedData({ data: normalizedData });
  const dataError = validateData?.(importData);
  const hasInvalidData =
    !!dataError ||
    error !== null ||
    data.length === 0 ||
    (strict &&
      fields.some(
        (field) => (field.required && !fieldMappings.current[field.value]) || hasColumnErrors(field.value),
      ));
  function handleClose() {
    setHasImportError(false);
    setOpen(false);
    setStep("upload");
    onClose?.();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isBusy) return;
        if (!isOpen) {
          handleClose();
          return;
        }
        setHasImportError(false);
        setOpen(true);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      {step === "upload" ? (
        <DialogContent className={cn("p-8 sm:max-w-xl", strict && "max-h-[90vh] overflow-y-auto")}>
          <DialogHeader>
            <DialogTitle>{uploadTitle ?? t("csv.uploadTitle")}</DialogTitle>
            <DialogDescription>{t("csv.uploadDescription")}</DialogDescription>
          </DialogHeader>
          {extraContent}
          <FileUploader
            accept={
              allowXlsx
                ? {
                    "text/csv": [".csv"],
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                  }
                : { "text/csv": [] }
            }
            multiple={false}
            maxSize={maxFileSize}
            maxFileCount={1}
            onValueChange={(files) => {
              const file = files[0];
              if (!file) return;

              setHasImportError(false);
              onParse({ file, limit: rowLimit });

              setStep("map");
            }}
            /**
             * alternatively this can be used with uploading the file
             */
            // onUpload={async (files) => {
            //   const file = files[0];
            //   if (!file) return;
            //   await onUpload(files);

            //   onParse({ file, limit: rowLimit });
            //   setStep("map");
            // }}
            // disabled={isUploading}
          />
        </DialogContent>
      ) : (
        <DialogContent
          className={cn("overflow-hidden p-8 sm:max-w-6xl", strict && "max-h-[90vh] overflow-y-auto")}
          isCloseDisabled={isBusy}
          onEscapeKeyDown={(event) => {
            if (isBusy) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (isBusy) event.preventDefault();
          }}
        >
          <div className="flex flex-col items-center gap-2 sm:flex-row">
            <DialogHeader className="flex-1">
              <DialogTitle>{t("csv.mapFieldsTitle")}</DialogTitle>
              <DialogDescription>{t("csv.mapFieldsDescription")}</DialogDescription>
            </DialogHeader>
            <Button
              variant="ghost"
              className="border"
              size="xs"
              disabled={isLocked}
              onClick={onFieldsReset}
              aria-label={t("csv.resetMappings")}
            >
              {t("actions.reset")}
            </Button>
          </div>
          <fieldset disabled={isLocked} className="grid h-105 min-w-0 w-full overflow-hidden rounded-md border">
            <Table className="border-b">
              <TableHeader className="shadow-sm sticky top-0 z-10 bg-background">
                <TableRow className="h-10 bg-muted/50">
                  {fields.map((field) => (
                    <PreviewTableHead
                      key={field.value}
                      field={field}
                      onFieldChange={(f) => {
                        onFieldChange({
                          oldValue: f.value,
                          newValue: field.value,
                        });
                      }}
                      onFieldToggle={onFieldToggle}
                      originalFieldMappings={fieldMappings.original}
                      currentFieldMapping={fieldMappings.current[field.value]}
                      hasErrors={hasColumnErrors(field.value)}
                      className="border-r"
                    />
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {normalizedData.map((row, i) => (
                  <TableRow key={i} className="h-8">
                    {fields.map((field) => (
                      <TableCell key={field.value} className="border-r last:border-r-0">
                        {field.options ? (
                          (() => {
                            const cellValue = String(row[field.value] ?? "");
                            const hasMatch = field.options!.some((o) => o.value === cellValue);
                            return (
                              <Select
                                value={hasMatch ? cellValue : ""}
                                onValueChange={(value) => onCellChange(i, field.value, value)}
                              >
                                <SelectTrigger
                                  className={cn(
                                    "h-7 border-none px-1 shadow-none",
                                    validationErrors[field.value]?.[i] && "text-red-500",
                                  )}
                                >
                                  <SelectValue placeholder={cellValue} />
                                </SelectTrigger>
                                <SelectContent>
                                  {field.options!.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          })()
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className={cn("line-clamp-1", validationErrors[field.value]?.[i] && "text-red-500")}
                                >
                                  {String(row[field.value] ?? "")}
                                </span>
                              </TooltipTrigger>
                              {validationErrors[field.value]?.[i] && (
                                <TooltipContent>
                                  <p>{validationErrors[field.value][i]}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </fieldset>
          {extraContent}
          {isBusy && (
            <p role="status" className="flex items-center gap-2 text-sm">
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              {t(isParsing ? "csv.reading" : "csv.importing")}
            </p>
          )}
          {(error || dataError || hasImportError) && (
            <p role="alert" className="text-sm text-destructive">
              {dataError ||
                (error === "rowLimitExceeded"
                  ? t("csv.rowLimitExceeded", { count: rowLimit })
                  : t("csv.invalidValue"))}
            </p>
          )}
          <DialogFooter className="gap-2 sm:space-x-0">
            <Button
              variant="ghost"
              className="border"
              size="xs"
              disabled={isLocked}
              onClick={() => setStep("upload")}
              aria-label={t("csv.backToUpload")}
            >
              {t("actions.back")}
            </Button>
            <Button
              className="border md:w-28"
              size="xs"
              disabled={isLocked || hasInvalidData}
              onClick={async () => {
                setLoading(true);
                setHasImportError(false);
                await new Promise((resolve) => setTimeout(resolve, 100));
                try {
                  await onImport(importData, ({ success, message }) => {
                    setLoading(false);
                    if (success) {
                      handleClose();
                    }
                    if (!success && message) {
                      console.error(message);
                    }
                  });
                } catch {
                  setLoading(false);
                  setHasImportError(true);
                }
              }}
              aria-label={t("csv.importData")}
            >
              {loading ? <Loader2 className="size-4 animate-spin" /> : t("actions.import")}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}

interface PreviewTableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  field: {
    label: string;
    value: string;
    required?: boolean;
    validation?: (cellValue: string) => { success: boolean; message?: string };
    note?: string;
  };
  onFieldChange: (props: { value: string; required?: boolean }) => void;
  onFieldToggle: (props: { value: string; checked: boolean }) => void;
  currentFieldMapping: string | undefined;
  originalFieldMappings: Record<string, string | undefined>;
  hasErrors?: boolean;
}

function PreviewTableHead({
  field,
  onFieldChange,
  onFieldToggle,
  currentFieldMapping,
  originalFieldMappings,
  hasErrors,
  className,
  ...props
}: PreviewTableHeadProps) {
  const { t } = useTranslation("common");
  const id = React.useId();
  const [open, setOpen] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    // Try to auto-map the field if it's not already mapped
    if (initialized) {
      return;
    }
    if (Object.keys(originalFieldMappings).length === 0) {
      return;
    }
    setInitialized(true);
    for (const fm of [...new Set(Object.values(originalFieldMappings))]) {
      if (fm?.toLowerCase() === field.label.toLowerCase()) {
        onFieldChange({
          value: fm ?? "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalFieldMappings]);

  return (
    <TableHead className={cn("items-start whitespace-nowrap py-2", className, hasErrors && "text-red-500")} {...props}>
      <div className="flex items-center gap-4 self-start pr-1.5 ">
        <div className="flex items-center gap-2">
          <Checkbox
            className="rounded-md border border-slate-300"
            id={`${id}-${field.value}`}
            defaultChecked
            onCheckedChange={(checked) => {
              onFieldToggle({
                value: field.value,
                checked: !!checked,
              });
            }}
            disabled={field.required}
          />
          <div className="flex flex-col">
            <Label htmlFor={`${id}-${field.value}`} className="truncate">
              {field.label}
              {hasErrors && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ExclamationTriangleIcon className="ml-1 inline-block size-4" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("csv.fieldContainsInvalidValues")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </Label>
          </div>
        </div>
        <ArrowLeftIcon className="size-4" aria-hidden="true" />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="xs"
              role="combobox"
              aria-expanded={open}
              className="w-48 grow  justify-between"
            >
              {currentFieldMapping || t("csv.selectField")}
              <CaretSortIcon className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
            <Command>
              <CommandInput placeholder={t("csv.searchField")} />
              <CommandEmpty>{t("csv.noFieldFound")}</CommandEmpty>
              <CommandList>
                <CommandGroup>
                  {[...new Set(Object.values(originalFieldMappings))].map((fm) => (
                    <CommandItem
                      key={fm}
                      value={fm}
                      onSelect={() => {
                        onFieldChange({
                          value: fm ?? "",
                        });
                        setOpen(false);
                      }}
                    >
                      <CheckIcon
                        className={cn("mr-2 size-4", currentFieldMapping === fm ? "opacity-100" : "opacity-0")}
                      />
                      <span className="line-clamp-1">{fm}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      {field.note ? (
        <>
          <span className="text-xs text-muted-foreground">{t("csv.note")} </span>
          <span className="text-xs font-normal text-muted-foreground">{field.note}</span>
        </>
      ) : (
        <span className="text-xs text-muted-foreground"> &nbsp; </span>
      )}
    </TableHead>
  );
}
